// File: src/pages/Staff/ClockInQRPage.jsx

import React, { useState, useEffect } from 'react';
import QrScanner from 'react-qr-scanner';
import axios from '@/lib/axios';

import { useAuth } from "@/context/AuthProvider";

const ClockInQRPage = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState('📲 Scan QR Code to Clock In or Out');
  const [scannedToken, setScannedToken] = useState(null);
  const [action, setAction] = useState('in');

  useEffect(() => {
    if (!window.isSecureContext) {
      setStatus('🔐 This feature requires a secure HTTPS connection.');
    }
  }, []);

  const getDeviceId = () => {
    let id = localStorage.getItem('device_id');
    if (!id) {
      id = 'device-' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('device_id', id);
    }
    return id;
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => {
          console.error('📡 GPS error:', err);
          reject(err);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleScan = async (result) => {
    const token = result?.text || result;
    if (!token || token === scannedToken) return;
    setScannedToken(token);
    setStatus('🔍 Verifying QR Token...');

    try {
      if (!user?.staff_id) {
        setStatus('⚠️ You must be logged in to clock in/out.');
        return;
      }

      const gps = await getLocation();
      const payload = {
        staff_id: user.staff_id,
        token,
        action,
        device_id: getDeviceId(),
        location: gps,
        timestamp: new Date().toISOString(),
      };

      const response = await axios.post('/api/secure-clock/qr', payload);
      setStatus(`✅ ${response.data.message}`);
    } catch (err) {
      if (!err.response) {
        const offlineQueue = JSON.parse(localStorage.getItem('offline_clock_queue') || '[]');
        offlineQueue.push({
          staff_id: user.staff_id,
          token,
          action,
          device_id: getDeviceId(),
          location: await getLocation(),
          timestamp: new Date().toISOString(),
        });
        localStorage.setItem('offline_clock_queue', JSON.stringify(offlineQueue));
        setStatus('📴 Offline — Clock-in saved and will retry when online.');
        return;
      }

      const errorMsg = err.response.data?.error || 'Clock-in failed. Try again.';
      if (errorMsg.includes('expired')) {
        setStatus('⏳ QR code expired. Please rescan.');
        setScannedToken(null);
      } else {
        setStatus(`❌ ${errorMsg}`);
      }
    }
  };

  const handleError = (err) => {
    console.error('📷 QR scanner error:', err);
    setStatus('❌ Unable to access camera or QR reader.');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white px-4 py-8">
      <h1 className="text-2xl font-bold mb-4 text-green-400">Smart QR Clock</h1>

      <div className="mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={action === 'out'}
            onChange={(e) => setAction(e.target.checked ? 'out' : 'in')}
            className="form-checkbox h-5 w-5 text-green-500"
          />
          <span className="text-sm">Toggle to Clock Out</span>
        </label>
      </div>

      <div className="w-full max-w-xs border-2 border-green-400 rounded-xl overflow-hidden shadow-lg">
        <QrScanner
          delay={300}
          style={{ width: '100%' }}
          onError={handleError}
          onScan={handleScan}
        />
      </div>

      <p className="mt-6 text-center text-green-300 font-semibold">{status}</p>
      <p className="mt-2 text-xs text-gray-400 text-center">
        Please allow camera and location permissions.
      </p>
    </div>
  );
};

export default ClockInQRPage;
